/*
 * Copyright Higharc Inc. 2019
 * Partially ported to JavaScript/GLSL from https://github.com/google/spherical-harmonics which was licensed under Apache License Version 2.0
 * We use the same SH constants as Google's SH library.
 */
// In the following functions, SH coefficients are given in the following order:
// l=0 m=0
// l=1 m=-1
// l=1 m=0
// l=1 m=1
// l=2 m=-2
// l=2 m=-1
// l=2 m=0
// l=2 m=1
// l=2 m=2
import { ShaderChunk } from "three";
import { SH } from "./SH";

// Constant declarations used by SH basis function definitions in GLSL.
// Array indexed by [ l ]
const glslBasisFunctionConstants = [
  `const float shc0 = 0.282095;`,

  `const float shc1 = 0.488603;`,

  `const float shc2 = 1.092548;
	 const float shc3 = 0.315392;
	 const float shc4 = 0.546274;`,

  `const float shc5 = 0.590044;
	 const float shc6 = 2.890611;
	 const float shc7 = 0.457046;
	 const float shc8 = 0.373176;
	 const float shc9 = 1.445306;`,

  `const float shc10 = 2.503343;
	 const float shc11 = 1.770131;
	 const float shc12 = 0.946175;
	 const float shc13 = 0.669047;
	 const float shc14 = 0.105786;
	 const float shc15 = 0.473087;
	 const float shc16 = 0.625836;`,
];

// SH basis function expressions in GLSL.
// Nested arrays indexed by [ l ][ m + l ]
const glslBasisFunctions = [
  [
    "  shc0", // l = 0, m = 0
  ],

  [
    "(-shc1) * sampleVector.y", // l = 1, m = -1
    "  shc1  * sampleVector.z", // l = 1, m = 0
    "(-shc1) * sampleVector.x", // l = 1, m = 1
  ],

  [
    "  shc2  * sampleVector.x * sampleVector.y", // l = 2, m = -2
    "(-shc2) * sampleVector.y * sampleVector.z", // l = 2, m = -1
    "  shc3  * (-squaredVector.x - squaredVector.y + squaredVector.z * 2.0)", // l = 2, m = 0
    "(-shc2) * sampleVector.x * sampleVector.z", // l = 2, m = 1
    "  shc4  * (squaredVector.x - squaredVector.y)", // l = 2, m = 2
  ],

  [
    "(-shc5) * sampleVector.y * (3.0 * squaredVector.x - squaredVector.y)", // l = 3, m = -3
    "  shc6  * sampleVector.x * sampleVector.y * sampleVector.z", // l = 3, m = -2
    "(-shc7) * sampleVector.y * (4.0 * squaredVector.z - squaredVector.x - squaredVector.y)", // l = 3, m = -1
    "  shc8  * sampleVector.z * (2.0 * squaredVector.z - 3.0 * squaredVector.x - 3.0 * squaredVector.y)", // l = 3, m = 0
    "(-shc7) * sampleVector.x * (4.0 * squaredVector.z - squaredVector.x - squaredVector.y)", // l = 3, m = 1
    "  shc9  * sampleVector.z * (squaredVector.x - squaredVector.y)", // l = 3, m = 2
    "(-shc5) * sampleVector.x * (squaredVector.x - 3.0 * squaredVector.y)", // l = 3, m = 3
  ],

  [
    "  shc10  * sampleVector.x * sampleVector.y * (squaredVector.x - squaredVector.y)", // l = 4, m = -4
    "(-shc11) * sampleVector.y * sampleVector.z * (3.0 * squaredVector.x - squaredVector.y)", // l = 4, m = -3
    "  shc12  * sampleVector.x * sampleVector.y * (7.0 * squaredVector.z - 1.0)", // l = 4, m = -2
    "(-shc13) * sampleVector.y * sampleVector.z * (7.0 * squaredVector.z - 3.0)", // l = 4, m = -1
    "  shc14  * (35.0 * squaredVector.z * squaredVector.z - 30.0 * squaredVector.z + 3.0)", // l = 4, m = 0
    "(-shc13) * sampleVector.x * sampleVector.z * (7.0 * squaredVector.z - 3.0)", // l = 4, m = 1
    "  shc15  * (squaredVector.x - squaredVector.y) * (7.0 * squaredVector.z - 1.0)", // l = 4, m = 2
    "(-shc11) * sampleVector.x * sampleVector.z * (squaredVector.x - 3.0 * squaredVector.y)", // l = 4, m = 3
    "  shc16  * (squaredVector.x * (squaredVector.x - 3.0 * squaredVector.y) - squaredVector.y * (3.0 * squaredVector.x - squaredVector.y))", // l = 4, m = 4
  ],
];

const maxShOrder = 4;

const generateEvalFunc = function (shOrder) {
  const shCoeffCount = SH.getCoefficientVectorCount(shOrder);

  let precalc = "";
  const basisFunctions: string[] = [];
  let coeffIndex = 0;
  for (let l = 0; l <= shOrder; ++l) {
    precalc += glslBasisFunctionConstants[l] + "\n";
    if (l === 2) {
      precalc += "vec3 squaredVector = sampleVector * sampleVector;\n";
    }
    for (let m = -l; m <= l; ++m) {
      basisFunctions.push("coeffs[" + coeffIndex + "] * " + glslBasisFunctions[l][m + l]);
      ++coeffIndex;
    }
  }

  return `vec4 evalRGBASH( vec4 coeffs[${shCoeffCount}], vec3 sampleVector ) {

		${precalc}

		return ${basisFunctions.join(" +\n")};
	}`;
};

const generateMixFunc = function (shOrder) {
  const shCoeffCount = SH.getCoefficientVectorCount(shOrder);

  return `void mixRGBASH( vec4 coeffsA[${shCoeffCount}], vec4 coeffsB[${shCoeffCount}], out vec4 interpolated[${shCoeffCount}], float a ) {

		for ( int i = 0; i < ${shCoeffCount}; ++i ) {
			interpolated[i] = mix(coeffsA[i], coeffsB[i], a);
		}

	}`;
};

// Generates "addSampleToSHCoefficients" that adds a vec4 sample from a given direction to an array of SH coefficients.
// The "sampleValue" parameter should be pre-weighted, and the sum of all weights should be the solid angle covering the whole sphere (4 PI).
const generateAddSampleToCoefficientsFunc = function (shOrder) {
  const shCoeffCount = SH.getCoefficientVectorCount(shOrder);

  let precalc = "";
  const addToCoefficients: string[] = [];

  let coeffIndex = 0;
  for (let l = 0; l <= shOrder; ++l) {
    precalc += glslBasisFunctionConstants[l] + "\n";
    if (l === 2) {
      precalc += "vec3 squaredVector = sampleVector * sampleVector;\n";
    }
    for (let m = -l; m <= l; ++m) {
      addToCoefficients.push("coeffs[" + coeffIndex + "] += " + glslBasisFunctions[l][m + l] + " * sampleValue;");
      ++coeffIndex;
    }
  }

  return `void addSampleToSHCoefficients( inout vec4 coeffs[ ${shCoeffCount} ], vec3 sampleVector, vec4 sampleValue ) {

		${precalc}

		${addToCoefficients.join("\n")}

	}`;
};

const generateEvalBasisFunctionFunc = function (shOrder) {
  let precalc = "";
  const evalBasisFunction: string[] = [];

  let coeffIndex = 0;
  // TODO: Some other structure would probably be faster here, like splitting the basis functions into common components. On WebGL 2 we could use switch/case.
  for (let l = 0; l <= shOrder; ++l) {
    precalc += glslBasisFunctionConstants[l] + "\n";
    if (l === 2) {
      precalc += "vec3 squaredVector = sampleVector * sampleVector;\n";
    }
    for (let m = -l; m <= l; ++m) {
      evalBasisFunction.push(
        "if ( basisFunctionIndex == " + coeffIndex + " ) return " + glslBasisFunctions[l][m + l] + ";",
      );
      ++coeffIndex;
    }
  }

  return `float evalSHBasisFunction${shOrder}( vec3 sampleVector, int basisFunctionIndex ) {

		${precalc}

		${evalBasisFunction.join("\n")}

		return 0.0;

	}`;
};

let sphericalHarmonicsHelpers = "";

for (let shOrder = 0; shOrder <= maxShOrder; ++shOrder) {
  sphericalHarmonicsHelpers += generateEvalFunc(shOrder) + "\n\n";
  sphericalHarmonicsHelpers += generateMixFunc(shOrder) + "\n\n";
  sphericalHarmonicsHelpers += generateAddSampleToCoefficientsFunc(shOrder) + "\n\n";
  sphericalHarmonicsHelpers += generateEvalBasisFunctionFunc(shOrder) + "\n\n";
}

ShaderChunk["spherical_harmonics_helpers"] = sphericalHarmonicsHelpers;

const generateProbeFuncs = function (shOrder) {
  const shCoeffCount = SH.getCoefficientVectorCount(shOrder);

  return `void getSHLightProbeCoefficients ( sampler2D probeData, ivec3 gridCoordinates, ivec3 probeCount, out vec4 coeffs[${shCoeffCount}] ) {

		int probeIndex = lightProbeIndex( gridCoordinates, probeCount );
		int totalProbeCount = probeCount.x * probeCount.y * probeCount.z;
		vec2 probeDataTexCoords = vec2( 0.5 / ${shCoeffCount}.0, (float(probeIndex) + 0.5) / float(totalProbeCount) );
		for ( int i = 0; i < ${shCoeffCount}; ++i ) {
			coeffs[i] = texture2D( probeData, probeDataTexCoords );
			probeDataTexCoords.x += 1.0 / ${shCoeffCount}.0;
		}

	}

	// Do a fetch, multiply and add of SH coefficients sampled from "probeData" into "coeffs". "mul" is used as a multiplier for the coefficients.
	void fmaSHLightProbeCoefficients ( sampler2D probeData, ivec3 gridCoordinates, ivec3 probeCount, inout vec4 coeffs[${shCoeffCount}], float mul ) {

		int probeIndex = lightProbeIndex( gridCoordinates, probeCount );
		int totalProbeCount = probeCount.x * probeCount.y * probeCount.z;
		vec2 probeDataTexCoords = vec2( 0.5 / ${shCoeffCount}.0, (float(probeIndex) + 0.5) / float(totalProbeCount) );
		for ( int i = 0; i < ${shCoeffCount}; ++i ) {
			coeffs[i] += texture2D( probeData, probeDataTexCoords ) * mul;
			probeDataTexCoords.x += 1.0 / ${shCoeffCount}.0;
		}

	}

	void getNearestSHCoefficientsFromGrid( sampler2D probeData, vec3 worldCoordinates, ivec3 probeCount, mat4 worldToGridMatrix, out vec4 coeffs[${shCoeffCount}] ) {

		vec4 worldPointHomogenous = vec4( worldCoordinates, 1.0 );
		vec3 gridCoordinates = ( worldToGridMatrix * worldPointHomogenous ).xyz;
		getSHLightProbeCoefficients( probeData, ivec3( clampCoordinatesToGrid( gridCoordinates + 0.5, probeCount ) ), probeCount, coeffs );

	}

	// This variation of sampling light probe coefficients offsets the sampled position in the light probe grid by
	// the world normal of the shading point. If the shading point is occluded by AO, then we offset less since it's
	// likely that offsetting along the normal will hit neighboring geometry.
	// randomValue should be evenly distributed between 0 and 1.
	void sampleSHCoefficientsFromGrid( sampler2D probeData, vec3 worldCoordinates, vec3 worldNormal, float aoFactor, ivec3 probeCount, mat4 worldToGridMatrix, float randomValue, out vec4 coeffs[${shCoeffCount}] ) {

		vec4 worldPointHomogenous = vec4( worldCoordinates, 1.0 );
		vec3 gridCoordinates = ( worldToGridMatrix * worldPointHomogenous ).xyz;
		vec4 normalHomogenous = vec4( worldNormal, 0.0 );
		gridCoordinates += normalize( ( worldToGridMatrix * normalHomogenous ).xyz ) * probeGridNormalOffsetMultiplier * aoFactor;
		vec3 gridCoordinatesFloor = floor( gridCoordinates );
		float probeWeights[8];
		probeWeights[0] = LIGHTPROBE_WEIGHTING_FUNCTION( gridCoordinates, gridCoordinatesFloor + vec3( 0, 0, 0 ), worldNormal );
		probeWeights[1] = LIGHTPROBE_WEIGHTING_FUNCTION( gridCoordinates, gridCoordinatesFloor + vec3( 0, 0, 1 ), worldNormal );
		probeWeights[2] = LIGHTPROBE_WEIGHTING_FUNCTION( gridCoordinates, gridCoordinatesFloor + vec3( 0, 1, 0 ), worldNormal );
		probeWeights[3] = LIGHTPROBE_WEIGHTING_FUNCTION( gridCoordinates, gridCoordinatesFloor + vec3( 0, 1, 1 ), worldNormal );
		probeWeights[4] = LIGHTPROBE_WEIGHTING_FUNCTION( gridCoordinates, gridCoordinatesFloor + vec3( 1, 0, 0 ), worldNormal );
		probeWeights[5] = LIGHTPROBE_WEIGHTING_FUNCTION( gridCoordinates, gridCoordinatesFloor + vec3( 1, 0, 1 ), worldNormal );
		probeWeights[6] = LIGHTPROBE_WEIGHTING_FUNCTION( gridCoordinates, gridCoordinatesFloor + vec3( 1, 1, 0 ), worldNormal );
		probeWeights[7] = LIGHTPROBE_WEIGHTING_FUNCTION( gridCoordinates, gridCoordinatesFloor + vec3( 1, 1, 1 ), worldNormal );

		// Pick a probe from the 8 probes under the trilinear filter by random.
		vec3 randomPick = weighedRandomCubeCorner( probeWeights, randomValue );

		vec3 randomizedCoordinates = gridCoordinatesFloor + randomPick;

		getSHLightProbeCoefficients( probeData, ivec3( clampCoordinatesToGrid( randomizedCoordinates, probeCount ) ), probeCount, coeffs );

	}

	void getInterpolatedSHCoefficientsFromGrid( sampler2D probeData, vec3 worldCoordinates, vec3 worldNormal, float aoFactor, ivec3 probeCount, mat4 worldToGridMatrix, out vec4 coeffs[${shCoeffCount}] ) {

		vec4 worldPointHomogenous = vec4( worldCoordinates, 1.0 );
		vec3 gridCoordinates = ( worldToGridMatrix * worldPointHomogenous ).xyz;
		vec4 normalHomogenous = vec4( worldNormal, 0.0 );
		gridCoordinates += normalize( ( worldToGridMatrix * normalHomogenous ).xyz ) * probeGridNormalOffsetMultiplier * aoFactor;
		vec3 gridCoordinatesFloor = floor( gridCoordinates );
		float probeWeights[8];
		probeWeights[0] = LIGHTPROBE_WEIGHTING_FUNCTION( gridCoordinates, gridCoordinatesFloor + vec3( 0, 0, 0 ), worldNormal );
		probeWeights[1] = LIGHTPROBE_WEIGHTING_FUNCTION( gridCoordinates, gridCoordinatesFloor + vec3( 0, 0, 1 ), worldNormal );
		probeWeights[2] = LIGHTPROBE_WEIGHTING_FUNCTION( gridCoordinates, gridCoordinatesFloor + vec3( 0, 1, 0 ), worldNormal );
		probeWeights[3] = LIGHTPROBE_WEIGHTING_FUNCTION( gridCoordinates, gridCoordinatesFloor + vec3( 0, 1, 1 ), worldNormal );
		probeWeights[4] = LIGHTPROBE_WEIGHTING_FUNCTION( gridCoordinates, gridCoordinatesFloor + vec3( 1, 0, 0 ), worldNormal );
		probeWeights[5] = LIGHTPROBE_WEIGHTING_FUNCTION( gridCoordinates, gridCoordinatesFloor + vec3( 1, 0, 1 ), worldNormal );
		probeWeights[6] = LIGHTPROBE_WEIGHTING_FUNCTION( gridCoordinates, gridCoordinatesFloor + vec3( 1, 1, 0 ), worldNormal );
		probeWeights[7] = LIGHTPROBE_WEIGHTING_FUNCTION( gridCoordinates, gridCoordinatesFloor + vec3( 1, 1, 1 ), worldNormal );

		float weightTotal = 0.0;
		for ( int i = 0; i < 8; ++i ) {
			weightTotal += probeWeights[i];
		}
		float invWeightTotal = 1.0 / weightTotal;

		gridCoordinatesFloor = clampCoordinatesToGrid( gridCoordinatesFloor, probeCount );
		vec3 gridCoordinatesCeil = clampCoordinatesToGrid( ceil(gridCoordinates), probeCount );

		for ( int i = 0; i < ${shCoeffCount}; ++i ) {
			coeffs[i] = vec4( 0.0 );
		}

		// 0, 0, 0
		fmaSHLightProbeCoefficients( probeData, ivec3( gridCoordinatesFloor ), probeCount, coeffs, probeWeights[0] * invWeightTotal );

		// 0, 0, 1
		fmaSHLightProbeCoefficients( probeData, ivec3( gridCoordinatesFloor.x, gridCoordinatesFloor.y, gridCoordinatesCeil.z ), probeCount, coeffs, probeWeights[1] * invWeightTotal );

		// 0, 1, 0
		fmaSHLightProbeCoefficients( probeData, ivec3( gridCoordinatesFloor.x, gridCoordinatesCeil.y, gridCoordinatesFloor.z ), probeCount, coeffs, probeWeights[2] * invWeightTotal );

		// 0, 1, 1
		fmaSHLightProbeCoefficients( probeData, ivec3( gridCoordinatesFloor.x, gridCoordinatesCeil.y, gridCoordinatesCeil.z ), probeCount, coeffs, probeWeights[3] * invWeightTotal );

		// 1, 0, 0
		fmaSHLightProbeCoefficients( probeData, ivec3( gridCoordinatesCeil.x, gridCoordinatesFloor.y, gridCoordinatesFloor.z ), probeCount, coeffs, probeWeights[4] * invWeightTotal );

		// 1, 0, 1
		fmaSHLightProbeCoefficients( probeData, ivec3( gridCoordinatesCeil.x, gridCoordinatesFloor.y, gridCoordinatesCeil.z ), probeCount, coeffs, probeWeights[5] * invWeightTotal );

		// 1, 1, 0
		fmaSHLightProbeCoefficients( probeData, ivec3( gridCoordinatesCeil.x, gridCoordinatesCeil.y, gridCoordinatesFloor.z ), probeCount, coeffs, probeWeights[6] * invWeightTotal );

		// 1, 1, 1
		fmaSHLightProbeCoefficients( probeData, ivec3( gridCoordinatesCeil ), probeCount, coeffs, probeWeights[7] * invWeightTotal );

	}

	// Store trilinearly interpolated coefficients from an interleaved light probe grid to the coeffs array.
	void getInterpolatedSHCoefficientsFromInterleavedGrid( sampler2D probeData, vec3 worldCoordinates, vec3 worldNormal, float aoFactor, ivec3 probeCount, mat4 worldToGridMatrix, out vec4 coeffs[${shCoeffCount}] ) {
		vec4 worldPointHomogenous = vec4( worldCoordinates, 1.0 );
		vec3 gridCoordinates = ( worldToGridMatrix * worldPointHomogenous ).xyz;
		vec4 normalHomogenous = vec4( worldNormal, 0.0 );
		gridCoordinates += normalize( ( worldToGridMatrix * normalHomogenous ).xyz ) * probeGridNormalOffsetMultiplier * aoFactor;
		vec3 gridCoordinatesFloor = clampCoordinatesToGrid( floor( gridCoordinates ), probeCount );
		vec3 gridCoordinatesCeil = clampCoordinatesToGrid( ceil( gridCoordinates ), probeCount );
		gridCoordinates = clampCoordinatesToGrid( gridCoordinates, probeCount );

		float maxZWeight = gridCoordinates.z - gridCoordinatesFloor.z;
		vec2 pixelSizeInTexCoords = 1.0 / vec2( probeCount.x * probeCount.z, probeCount.y * ${shCoeffCount} );

		for ( int coeffIndex = 0; coeffIndex < ${shCoeffCount}; ++coeffIndex ) {
			vec2 texCoordinates = getInterleavedSHGridPixelCoordinates( gridCoordinates.xy, int( gridCoordinatesFloor.z ), coeffIndex, probeCount ) * pixelSizeInTexCoords;
			vec4 coeffMinZ = texture2D( probeData, texCoordinates );
			texCoordinates = getInterleavedSHGridPixelCoordinates( gridCoordinates.xy, int( gridCoordinatesCeil.z ), coeffIndex, probeCount ) * pixelSizeInTexCoords;
			vec4 coeffMaxZ = texture2D( probeData, texCoordinates );
			coeffs[coeffIndex] = mix( coeffMinZ, coeffMaxZ, maxZWeight );
		}
	}
	`;
};

let lightProbeGridHelpers = `// Return the pixel coordinates for the given light probe grid coordinates in a light probe atlas with interleaved packing.
	vec2 getInterleavedSHGridPixelCoordinates( vec2 gridCoordinatesXY, int gridCoordinatesZ, int coeffIndex, ivec3 probeCount ) {
		return vec2( gridCoordinatesZ * probeCount.x, coeffIndex * probeCount.y ) + gridCoordinatesXY + 0.5;
	}`;

for (let shOrder = 0; shOrder <= maxShOrder; ++shOrder) {
  lightProbeGridHelpers += "\n\n" + generateProbeFuncs(shOrder);
}

ShaderChunk["spherical_harmonics_light_probe_grid_helpers"] = lightProbeGridHelpers;
